// src/pages/NotificationManagement.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
  CircularProgress,
  Autocomplete,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  TablePagination
} from '@mui/material';
import {
  Send as SendIcon,
  NotificationsActive as NotificationsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useThemeSettings } from '../contexts/ThemeContext';
import { usersApi, companiesApi, notificationsApi } from '../services/apiClient';
import { formatDistanceToNow } from 'date-fns';

const NotificationManagement = () => {
  const { t } = useThemeSettings();
  const { enqueueSnackbar } = useSnackbar();

  // Form state
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState('normal');
  const [type, setType] = useState('info');
  const [recipient, setRecipient] = useState('all'); // all, company, specific
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showInBar, setShowInBar] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [overrideMute, setOverrideMute] = useState(false);
  const [actionUrl, setActionUrl] = useState('');

  // Data state
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);

  // Sent notifications state
  const [sentNotifications, setSentNotifications] = useState([]);
  const [sentNotificationsLoading, setSentNotificationsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalNotifications, setTotalNotifications] = useState(0);

  // Edit/Delete dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editPriority, setEditPriority] = useState('normal');
  const [editType, setEditType] = useState('info');

  useEffect(() => {
    loadData();
    loadSentNotifications();
  }, []);

  useEffect(() => {
    loadSentNotifications();
  }, [page, rowsPerPage]);

  const loadData = async () => {
    try {
      setDataLoading(true);
      const [companiesRes, usersRes] = await Promise.all([
        companiesApi.getAll(),
        usersApi.getCompanyUsers()
      ]);
      setCompanies(companiesRes.data.companies || []);
      setUsers(usersRes.data.users || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      enqueueSnackbar('Failed to load companies and users', { variant: 'error' });
    } finally {
      setDataLoading(false);
    }
  };

  const loadSentNotifications = async () => {
    try {
      setSentNotificationsLoading(true);
      const response = await notificationsApi.getAllSentNotifications({
        limit: rowsPerPage,
        offset: page * rowsPerPage
      });
      setSentNotifications(response.data.notifications || []);
      setTotalNotifications(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load sent notifications:', error);
      enqueueSnackbar('Failed to load sent notifications', { variant: 'error' });
    } finally {
      setSentNotificationsLoading(false);
    }
  };

  const handleSendNotification = async () => {
    if (!title || !body) {
      enqueueSnackbar(t ? t('NotificationManagement.fill_required') : 'Please fill in title and message', { variant: 'warning' });
      return;
    }

    try {
      setLoading(true);

      const notificationData = {
        title,
        body,
        priority: priority === 'urgent' ? 'critical' : priority, // Map urgent to critical
        type: type === 'system' ? 'system' : type,
        showInBar,
        sendEmail: sendEmail || priority === 'urgent', // Always send email for urgent
        overrideMute: overrideMute || priority === 'urgent', // Always override mute for urgent
        actionUrl: actionUrl || undefined,
        recipient,
        companyIds: recipient === 'company' ? selectedCompanies.map(c => c.id) : undefined,
        userIds: recipient === 'specific' ? selectedUsers.map(u => u.id) : undefined
      };

      // Use the API client which handles token automatically
      const response = await notificationsApi.sendNotification(notificationData);

      if (response.data.success) {
        enqueueSnackbar(
          t
            ? t('NotificationManagement.sent_success').replace('{count}', response.data.sentCount || 'all')
            : `Notification sent to ${response.data.sentCount || 'all'} recipient(s)`,
          { variant: 'success' }
        );

        // Reset form
        setTitle('');
        setBody('');
        setPriority('normal');
        setType('info');
        setRecipient('all');
        setSelectedCompanies([]);
        setSelectedUsers([]);
        setShowInBar(false);
        setSendEmail(false);
        setOverrideMute(false);
        setActionUrl('');

        // Reload sent notifications list
        loadSentNotifications();
      }
    } catch (error) {
      console.error('Failed to send notification:', error);
      enqueueSnackbar(
        error.response?.data?.message || (t ? t('NotificationManagement.send_failed') : 'Failed to send notification'),
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTestOverlay = () => {
    // Trigger a test notification event
    const testNotification = {
      id: Date.now(),
      title: title || 'Test Notification',
      body: body || 'This is a test notification to preview the desktop overlay',
      priority: priority,
      type: type,
      created_at: new Date().toISOString()
    };

    // Dispatch a custom event that the overlay can listen to
    window.dispatchEvent(new CustomEvent('test-notification', {
      detail: testNotification
    }));

    enqueueSnackbar('Test notification triggered! Check the top-right corner.', { variant: 'info' });
  };

  const handleEditClick = (notification) => {
    setSelectedNotification(notification);
    setEditTitle(notification.title);
    setEditBody(notification.body || notification.message);
    setEditPriority(notification.priority === 'critical' ? 'urgent' : notification.priority);
    setEditType(notification.type);
    setEditDialogOpen(true);
  };

  const handleUpdateNotification = async () => {
    if (!editTitle || !editBody) {
      enqueueSnackbar('Please fill in title and message', { variant: 'warning' });
      return;
    }

    try {
      setLoading(true);
      await notificationsApi.updateNotification(selectedNotification.id, {
        title: editTitle,
        body: editBody,
        priority: editPriority === 'urgent' ? 'critical' : editPriority,
        type: editType
      });

      enqueueSnackbar('Notification updated successfully', { variant: 'success' });
      setEditDialogOpen(false);
      loadSentNotifications(); // Reload the list
    } catch (error) {
      console.error('Failed to update notification:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to update notification',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (notification) => {
    setSelectedNotification(notification);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      await notificationsApi.deleteNotification(selectedNotification.id);
      enqueueSnackbar('Notification deleted successfully', { variant: 'success' });
      setDeleteDialogOpen(false);
      loadSentNotifications(); // Reload the list
    } catch (error) {
      console.error('Failed to delete notification:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to delete notification',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
      case 'urgent':
        return '#f44336';
      case 'high':
        return '#ff9800';
      case 'normal':
        return '#2196f3';
      case 'low':
        return '#4caf50';
      default:
        return '#2196f3';
    }
  };

  if (dataLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          {t ? t('NotificationManagement.title') : 'Send System Notification'}
        </Typography>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          {t ? t('NotificationManagement.info_message') : 'Send notifications to all users, specific companies, or individual users. Urgent notifications will automatically send emails and override device mute settings.'}
        </Alert>

        {/* Title */}
        <TextField
          fullWidth
          label={t ? t('NotificationManagement.notification_title') : 'Notification Title'}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 2 }}
          required
        />

        {/* Body */}
        <TextField
          fullWidth
          multiline
          rows={4}
          label={t ? t('NotificationManagement.notification_message') : 'Notification Message'}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          sx={{ mb: 2 }}
          required
        />

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          {/* Priority */}
          <FormControl fullWidth>
            <InputLabel>{t ? t('Notifications.priority') : 'Priority'}</InputLabel>
            <Select
              value={priority}
              label={t ? t('Notifications.priority') : 'Priority'}
              onChange={(e) => {
                const newPriority = e.target.value;
                setPriority(newPriority);
                // Auto-enable email and override for urgent
                if (newPriority === 'urgent') {
                  setSendEmail(true);
                  setOverrideMute(true);
                  setType('system');
                  setShowInBar(true);
                }
              }}
            >
              <MenuItem value="low">{t ? t('Notifications.low') : 'Low'}</MenuItem>
              <MenuItem value="normal">{t ? t('Notifications.normal') : 'Normal'}</MenuItem>
              <MenuItem value="high">{t ? t('Notifications.high') : 'High'}</MenuItem>
              <MenuItem value="urgent">{t ? t('Notifications.critical_urgent') : 'Urgent (Critical)'}</MenuItem>
            </Select>
          </FormControl>

          {/* Type */}
          <FormControl fullWidth>
            <InputLabel>{t ? t('Common.type') : 'Type'}</InputLabel>
            <Select
              value={type}
              label={t ? t('Common.type') : 'Type'}
              onChange={(e) => setType(e.target.value)}
            >
              <MenuItem value="info">{t ? t('Common.info') : 'Info'}</MenuItem>
              <MenuItem value="success">{t ? t('Common.success') : 'Success'}</MenuItem>
              <MenuItem value="warning">{t ? t('Common.warning') : 'Warning'}</MenuItem>
              <MenuItem value="error">{t ? t('Common.error') : 'Error'}</MenuItem>
              <MenuItem value="system">{t ? t('System Announcement') : 'System Announcement'}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Recipient Selection */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>{t ? t('NotificationManagement.send_to') : 'Send To'}</InputLabel>
          <Select
            value={recipient}
            label={t ? t('NotificationManagement.send_to') : 'Send To'}
            onChange={(e) => setRecipient(e.target.value)}
          >
            <MenuItem value="all">{t ? t('NotificationManagement.send_to_all') : 'All Users'}</MenuItem>
            <MenuItem value="company">{t ? t('NotificationManagement.send_to_company') : 'Specific Companies'}</MenuItem>
            <MenuItem value="specific">{t ? t('NotificationManagement.send_to_specific') : 'Specific Users'}</MenuItem>
          </Select>
        </FormControl>

        {/* Company Selection */}
        {recipient === 'company' && (
          <Autocomplete
            multiple
            options={companies}
            getOptionLabel={(option) => option.name}
            value={selectedCompanies}
            onChange={(e, newValue) => setSelectedCompanies(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t ? t('NotificationManagement.select_companies') : 'Select Companies'}
                placeholder={t ? t('Choose companies...') : 'Choose companies...'}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option.name} {...getTagProps({ index })} />
              ))
            }
            sx={{ mb: 2 }}
          />
        )}

        {/* User Selection */}
        {recipient === 'specific' && (
          <Autocomplete
            multiple
            options={users}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={selectedUsers}
            onChange={(e, newValue) => setSelectedUsers(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t ? t('NotificationManagement.select_users') : 'Select Users'}
                placeholder={t ? t('Choose users...') : 'Choose users...'}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option.name} {...getTagProps({ index })} />
              ))
            }
            sx={{ mb: 2 }}
          />
        )}

        {/* Action URL */}
        <TextField
          fullWidth
          label={t ? t('NotificationManagement.action_url_optional') : 'Action URL (optional)'}
          value={actionUrl}
          onChange={(e) => setActionUrl(e.target.value)}
          placeholder="/tickets/123"
          helperText={t ? t('NotificationManagement.action_url_help') : 'URL to navigate when notification is clicked'}
          sx={{ mb: 2 }}
        />

        <Divider sx={{ my: 2 }} />

        {/* Options */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showInBar}
                onChange={(e) => setShowInBar(e.target.checked)}
                disabled={priority === 'urgent'}
              />
            }
            label={t ? t('NotificationManagement.show_in_bar') : 'Show in notification bar (planned maintenance/downtime)'}
          />
          <FormControlLabel
            control={
              <Switch
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                disabled={priority === 'urgent'}
              />
            }
            label={t ? t('NotificationManagement.send_email') : 'Send email notification'}
          />
          <FormControlLabel
            control={
              <Switch
                checked={overrideMute}
                onChange={(e) => setOverrideMute(e.target.checked)}
                disabled={priority === 'urgent'}
              />
            }
            label={t ? t('NotificationManagement.override_mute') : 'Override mute settings (play sound even if device is muted)'}
          />
        </Box>

        {priority === 'urgent' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>{t ? t('NotificationManagement.urgent_warning') : 'Urgent notifications will automatically:'}</strong>
            <ul style={{ marginTop: 8, marginBottom: 0 }}>
              <li>{t ? t('NotificationManagement.urgent_feature_email') : 'Send email notifications'}</li>
              <li>{t ? t('NotificationManagement.urgent_feature_mute') : 'Override device mute settings'}</li>
              <li>{t ? t('NotificationManagement.urgent_feature_bar') : 'Show in notification bar'}</li>
              <li>{t ? t('NotificationManagement.urgent_feature_interaction') : 'Require user interaction to dismiss'}</li>
            </ul>
          </Alert>
        )}

        {/* Send Button */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            size="large"
            onClick={handleTestOverlay}
            disabled={!title || !body}
            sx={{ flex: 1 }}
          >
            Test Preview
          </Button>
          <Button
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
            onClick={handleSendNotification}
            disabled={loading || !title || !body}
            sx={{ flex: 2 }}
          >
            {loading
              ? (t ? t('NotificationManagement.sending') : 'Sending...')
              : (t ? t('NotificationManagement.send_notification') : 'Send Notification')}
          </Button>
        </Box>
      </Paper>

      {/* Sent Notifications History */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">
            {t ? t('NotificationManagement.sent_notifications_history') : 'Sent Notifications History'}
          </Typography>
          <Tooltip title="Refresh">
            <IconButton onClick={loadSentNotifications} disabled={sentNotificationsLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {sentNotificationsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : sentNotifications.length === 0 ? (
          <Alert severity="info">No sent notifications found</Alert>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Message</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Recipients</TableCell>
                    <TableCell>Sent</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sentNotifications.map((notification) => (
                    <TableRow key={notification.id}>
                      <TableCell>{notification.title}</TableCell>
                      <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {notification.body || notification.message}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={notification.priority}
                          size="small"
                          sx={{
                            backgroundColor: getPriorityColor(notification.priority),
                            color: 'white'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip label={notification.type} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        {notification.recipient === 'all' ? 'All Users' :
                         notification.recipient === 'company' ? `${notification.companyIds?.length || 0} Companies` :
                         notification.recipient === 'specific' ? `${notification.userIds?.length || 0} Users` :
                         notification.user ? '1 User' : 'Unknown'}
                      </TableCell>
                      <TableCell>
                        {notification.created_at
                          ? formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })
                          : 'Unknown'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEditClick(notification)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDeleteClick(notification)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalNotifications}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Notification</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Title"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Message"
            value={editBody}
            onChange={(e) => setEditBody(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={editPriority}
              label="Priority"
              onChange={(e) => setEditPriority(e.target.value)}
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="urgent">Urgent (Critical)</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              value={editType}
              label="Type"
              onChange={(e) => setEditType(e.target.value)}
            >
              <MenuItem value="info">Info</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="system">System Announcement</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateNotification} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Notification</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this notification? This action cannot be undone.
          </Typography>
          {selectedNotification && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                {selectedNotification.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedNotification.body || selectedNotification.message}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationManagement;
